import React from 'react';

import { format } from 'url';

import * as Sentry from '@sentry/nextjs';
import config from 'config';
import NextRouter from 'next/router';
import PropTypes from 'prop-types';
import { withCookies } from 'react-cookie';
import styled, { css } from 'styled-components';

import { Router } from '@techstyle/next-routes';
import { getMembership } from '@techstyle/react-accounts';
import { getDomainRedirectFunction, getSession } from '@techstyle/redux-core';

import createPage from '../registry';
import {
  loadProduct,
  loadProductAddedToBasketCount,
  loadProductByPermalink,
  loadProductSet,
  trackPdpView,
} from '../src/actions/products';
import { loadReviews } from '../src/actions/reviews';
import { Component as ByoExitModal } from '../src/components/ByoExitModal';
import { Component as ByoProvider } from '../src/components/ByoProvider';
import { Component as ByoStickyProgressBar } from '../src/components/ByoStickyProgressBar';
import { Component as Loading } from '../src/components/Loading';
import { Container as MainTemplate } from '../src/components/MainTemplate';
import { Container as ProductDetailLayout } from '../src/components/ProductDetailLayout';
import { Component as ProductHead } from '../src/components/ProductHead';
import { Component as SearchSpringTracking } from '../src/components/SearchSpringTracking';
import { Component as StyleContext } from '../src/components/StyleContext';
import ToastContext from '../src/components/ToastContext/ToastContext';
import { useBraSizeNavigation } from '../src/slices/braSizeSelections';
import { mobile } from '../src/styles';
import { getCountryRedirect } from '../src/utils/borderfree';
import formatProductUrl from '../src/utils/formatProductUrl';
import generateCdnImageUrl from '../src/utils/generateCdnImageUrl';
import {
  getDefaultSingleSelections,
  hasLinkedProduct,
} from '../src/utils/productDetail';
import { redirect } from '../src/utils/routing';
import {
  isPreorderable,
  isProductSet as getIsProductSet,
  isUnlockedXclusiveProductVisible as getIsUnlockedXclusiveVisible,
} from '../src/utils/selectors';
import { useOneTrustConsent } from '../src/utils/useOneTrustConsent';

import ErrorPage from './_error';

const unlockedXclusiveTag = config.get('public.productTags.unlockedXclusive');

const SETS = 'sets';

const Layout = styled.div`
  padding-top: ${props => props.theme.spacing.small}px;
`;

const ProductMainTemplate = styled(MainTemplate)`
  ${MainTemplate.Body} {
    ${mobile`
      z-index: ${props =>
        props.theme.zIndex.increment + (props.isModelImageFullScreen ? 3 : 1)};
    `};
  }

  ${MainTemplate.Footer} {
    ${mobile`
      padding-bottom: 80px;
    `};
  }

  ${MainTemplate.Header} {
    ${({ isModelImageFullScreen }) =>
      isModelImageFullScreen
        ? css`
            z-index: unset;
          `
        : ''}

    ${mobile`
      z-index: ${props => props.theme.zIndex.increment + 2};
    `};
  }
`;

export class ProductPage extends React.Component {
  static propTypes = {
    cookies: PropTypes.object,
    customerDetails: PropTypes.object,
    domain: PropTypes.object,
    geoCountry: PropTypes.string,
    getDomainRedirect: PropTypes.func,
    isBorderfreeCustomer: PropTypes.bool,
    isInfluencer: PropTypes.bool,
    isPreorderable: PropTypes.bool,
    isUnlockedXclusiveProductVisible: PropTypes.bool,
    isVip: PropTypes.bool,
    isVisitor: PropTypes.bool,
    loadProduct: PropTypes.func,
    loadProductByPermalink: PropTypes.func,
    loadProductSet: PropTypes.func,
    membershipLoaded: PropTypes.bool,
    onTrustConsentList: PropTypes.array,
    product: PropTypes.object,
    productType: PropTypes.string,
    query: PropTypes.object,
    reviews: PropTypes.object,
    ssrOriginPathname: PropTypes.string,
    trackPdpView: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      error: null,
      statusCode: null,
      isModelImageFullScreen: false,
      isLoading: false,
    };
  }

  hasOnetrustCookiesConsent() {
    const { oneTrustConsentList } = this.props;

    return (
      oneTrustConsentList.includes('C0002') ||
      oneTrustConsentList.includes('C0003') ||
      oneTrustConsentList.includes('C0004')
    );
  }

  componentDidMount() {
    const { cookies, product, domain, membershipLoaded, geoCountry } =
      this.props;
    const isCookiesAccepted =
      this.hasOnetrustCookiesConsent() ||
      cookies.get('acceptCookies') === 'true';
    const countryCode = cookies.get('bfx.country') || geoCountry;
    if ((isCookiesAccepted || countryCode === 'US') && product) {
      this.updateRecentlyViewedProducts(product);
    }

    if (domain.tld === '.com') {
      this.checkForCountryRedirect();
    }

    if (product && membershipLoaded) {
      this.trackProduct();
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    const {
      membershipLoaded,
      isVisitor,
      isVip,
      product,
      loadProduct,
      loadProductSet,
      isInfluencer,
      productType,
      query,
    } = this.props;

    const prevProduct = prevProps.product;
    const isProductDifferent =
      product &&
      (product.master_product_id !== prevProduct.master_product_id ||
        query.productId !== prevProps.query.productId);

    if (
      product &&
      prevProps.membershipLoaded !== membershipLoaded &&
      membershipLoaded
    ) {
      this.trackProduct();
    } else if (isProductDifferent) {
      this.trackProduct();
    }

    if (
      !this.state.isLoading &&
      !isProductDifferent &&
      (isVisitor !== prevProps.isVisitor || isVip !== prevProps.isVip)
    ) {
      this.setState({ isLoading: true });
      // This will result in calling the client-side version of`getInitialProps` so
      // that we have fresh product data.
      // It's a bit of a hacky workaround, but it's the best we can do for now without
      // refactoring how the PDP is structured/handles data fetching.
      // We need to use both instances of the Next router here because
      // `Router.asPath` does not exist, and `NextRouter.replace` causes an error.
      await Router.replace(NextRouter.asPath);
      this.setState({ isLoading: false });
      return;
    }

    if (
      (product &&
        membershipLoaded &&
        (isVisitor !== prevProps.isVisitor || isVip !== prevProps.isVip)) ||
      (product && isInfluencer !== prevProps.isInfluencer)
    ) {
      let results;

      if (productType === SETS) {
        results = await loadProductSet(product.master_product_id);
      } else {
        results = await loadProduct(product.master_product_id);
      }

      /** safety in case navigating on a new product while fetching**/
      const latestProduct = this.props.product;
      if (
        latestProduct &&
        latestProduct.master_product_id === product.master_product_id
      ) {
        if (!results.payload || results.error) {
          // Cannot load order from orderId
          this.setState({
            statusCode: 404,
            error: results.payload,
          });
        }
      }
    }
  }

  trackProduct = () => {
    const {
      customerDetails,
      isBorderfreeCustomer,
      isPreorderable,
      product,
      query,
      trackPdpView,
      reviews = {},
    } = this.props;
    const { gridPosition } = query;
    const {
      available_quantity_master: availableQuantityMaster,
      color: masterColor,
      component_product_id_object_list: componentProductIdObjectList,
      default_unit_price: defaultUnitPrice,
      item_number: itemNumber,
      label: masterLabel,
      master_product_id: masterProductId,
      products_in_set: productsInSet,
      retail_unit_price: masterRetailUnitPrice,
      meta_data: metaData = [],
      vimeo_id: vimeoId,
    } = product;

    const hasMemberModels = metaData.some(metaData =>
      Boolean(metaData.meta_description_override)
    );

    const { average_overall_review_filtered_stores: averageRating = 0 } =
      product;

    const { counts = {} } = reviews;

    const { reviewCount = 0, recommendedPercentage = 0 } = counts;

    const sharedTracking = {
      is_preorder: isPreorderable,
      avg_recommended: recommendedPercentage,
      review_num: reviewCount,
      has_reviews: Boolean(reviewCount),
      avg_star_rating: Number(averageRating.toFixed(1)),
      has_member_models: hasMemberModels,
    };

    const sizeCache = JSON.parse(localStorage.getItem('selectedSize'));
    const isProductSet = getIsProductSet(product);
    const imgUrl = generateCdnImageUrl({
      product,
      modelIndex: 0,
      angle: 1,
      imageSize: 3,
    });

    let promo = '';
    let price = defaultUnitPrice;
    if (isBorderfreeCustomer) {
      price = masterRetailUnitPrice;
    } else {
      if (product.promos) {
        let promos = product.promos;
        if (Array.isArray(product.promos) && product.promos.length > 0) {
          promos = product.promos[0];
        }
        if (promos.display_on_pdp) {
          promo = promos.promo_code;
          price = promos.promo_price;
        }
      }
    }

    const productInfoJSON = {
      category: product.default_product_category_id,
      coupon: promo,
      image_url: imgUrl.toString(),
      inventory_availability: availableQuantityMaster,
      is_bundle: isProductSet,
      list: query.psrc,
      price,
      position: parseInt(gridPosition),
      quantity: 1,
      sku: itemNumber.toString(),
      url: window.location.href,
      customer_id: customerDetails?.id?.toString(),
      has_video: !!vimeoId,
    };

    if (isProductSet) {
      productsInSet.forEach((productInSet, index) => {
        const {
          alias,
          color,
          label,
          master_product_id: productId,
        } = productInSet;

        const defaultSize = getDefaultSingleSelections(
          productInSet,
          customerDetails,
          false,
          sizeCache
        );
        const { value: sizeValue, sizeProduct } = defaultSize.size;
        const pricePercentageObject = componentProductIdObjectList.find(
          obj => obj.component_product_id === productId
        );
        const { price_contribution_percentage: priceContributionPercentage } =
          pricePercentageObject;

        trackPdpView({
          ...sharedTracking,
          ...productInfoJSON,
          bundle_alias: alias,
          bundle_index: index + 1,
          bundle_name: masterLabel,
          bundle_product_id: masterProductId.toString(),
          bundle_quantity: productsInSet.length,
          bundle_sale_price: defaultUnitPrice,
          bundle_retail_price: masterRetailUnitPrice,
          bundle_url: window.location.href,
          bundle_vip_price: defaultUnitPrice,
          label: masterProductId.toString(),
          name: label,
          product_id: productId.toString(),
          retail_price: parseFloat(
            (masterRetailUnitPrice * priceContributionPercentage).toFixed(2)
          ),
          sale_price: parseFloat(
            (defaultUnitPrice * priceContributionPercentage).toFixed(2)
          ),
          size: sizeValue,
          sized_product_id: sizeProduct
            ? sizeProduct.product_id.toString()
            : null,
          sized_sku: sizeProduct ? sizeProduct.item_number : null,
          variant: color,
          vip_price: parseFloat(
            (defaultUnitPrice * priceContributionPercentage).toFixed(2)
          ),
        });
      });
    } else {
      const defaultSize = getDefaultSingleSelections(
        product,
        customerDetails,
        false,
        sizeCache,
        isPreorderable
      );
      const { value: sizeValue, sizeProduct } = defaultSize.size;

      trackPdpView({
        ...sharedTracking,
        ...productInfoJSON,
        label: masterProductId.toString(),
        name: masterLabel,
        product_id: masterProductId.toString(),
        retail_price: masterRetailUnitPrice,
        sale_price: defaultUnitPrice,
        size: sizeValue,
        sized_product_id: sizeProduct
          ? sizeProduct.product_id.toString()
          : null,
        sized_sku: sizeProduct ? sizeProduct.item_number : null,
        variant: masterColor,
        vip_price: defaultUnitPrice,
      });
    }
  };

  async checkForCountryRedirect() {
    const {
      query,
      cookies,
      domain,
      loadProductByPermalink,
      getDomainRedirect,
      noRedirect,
    } = this.props;
    const { permalink } = query;
    const countryCode = cookies.get('bfx.country') || null;
    const shouldRedirect = !noRedirect || cookies.get('no_redirect') !== 'true';

    if (countryCode && shouldRedirect) {
      const redirectHost = getCountryRedirect(
        countryCode,
        domain,
        getDomainRedirect
      );
      const redirectTo = redirectHost ? redirectHost.replace(/\/$/, '') : null;
      if (redirectTo && redirectTo !== window.location.origin) {
        const countryDomains = config.get('public.borderfree.countryDomains');
        const storeGroups = config.get('public.domain.storeGroups');
        const countryDomain = countryDomains[countryCode];

        if (countryDomain) {
          const storeGroupId = storeGroups[countryDomain];
          if (storeGroupId) {
            const productPermalink = await loadProductByPermalink(permalink, {
              'x-tfg-storeGroupId': storeGroupId,
            });
            if (productPermalink.payload) {
              const { payload } = productPermalink;
              const { as } = formatProductUrl({
                productId: payload.master_product_id,
                permalink: payload.permalink,
              });
              window.location.href = `${redirectTo}${as}`;
            } else {
              window.location.href = redirectTo;
            }
          } else {
            window.location.href = redirectTo;
          }
        } else {
          window.location.href = redirectTo;
        }
      }
    }
  }

  updateRecentlyViewedProducts(newProduct) {
    const jsonData = localStorage.getItem('recently_viewed_products');

    const newProductData = {
      color: newProduct.color,
      item_number: newProduct.item_number,
      label: newProduct.label,
      master_product_id: newProduct.master_product_id,
      permalink: newProduct.permalink,
      product_type_id: newProduct.product_type_id,
      tag_id_list: newProduct.tag_id_list,
    };

    let products = [];

    if (jsonData) {
      try {
        products = JSON.parse(jsonData);
        if (!Array.isArray(products)) {
          products = [];
        }

        // newProductData previously stored items using camelCase keys,
        // which caused parts of the app to crash. We want to wipe out
        // that data if it exists.
        const isCamelCase = !!products.find(product =>
          Object.keys(product).includes('masterProductId')
        );
        if (isCamelCase) {
          products = [];
        }
      } catch (err) {
        products = [];
      }
    }
    if (products.length) {
      // Remove any products without product_type_id
      products = [
        newProductData,
        ...products
          .filter(product => product.product_type_id)
          .filter(
            product =>
              product.master_product_id !== newProductData.master_product_id
          ),
      ].slice(0, 4);
    } else {
      products = [newProductData];
    }

    try {
      localStorage.setItem(
        'recently_viewed_products',
        JSON.stringify(products)
      );
    } catch (err) {
      // Quota exceeded, do nothing.
    }
  }

  initializeTracking = () => {
    const { domain, product } = this.props;
    const siteIds = config.get('public.search.siteIds');
    const productSKU = product.item_number.replace(/-00$/, '');
    try {
      window.IntelliSuggest.init({
        siteId: siteIds[domain.tld],
        context: `Product/${productSKU}`,
        seed: [productSKU],
      });
      window.IntelliSuggest.viewItem({ sku: productSKU });
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  handleModelImageFullScreen = isModelImageFullScreen => {
    this.setState({ isModelImageFullScreen });
  };

  render() {
    const {
      isBorderfreeCustomer,
      isInfluencer,
      isUnlockedXclusiveProductVisible,
      isVip,
      membershipLoaded,
      productType,
      product,
      ssrOriginPathname,
    } = this.props;
    const { isModelImageFullScreen, statusCode, isLoading } = this.state;
    const trackingEnabled = config.get('public.search.trackingEnabled');

    const tagIdList = (product && product.tag_id_list) || [];
    // If someone is an EMP VIP who has not been billed this month, they
    // should not be able to see the product if it has an "Unlocked Xclusive"
    // tag.
    const shouldHideUnlockedXclusiveProduct =
      tagIdList.includes(unlockedXclusiveTag) &&
      !isUnlockedXclusiveProductVisible;

    if (!product || shouldHideUnlockedXclusiveProduct) {
      return <ErrorPage statusCode={statusCode || 404} />;
    }

    if (!membershipLoaded || isLoading) {
      return (
        <ByoProvider.Consumer>
          {({ isFlowInitiated }) => (
            <ProductMainTemplate
              hideNewsletterModal
              hideFooter={isFlowInitiated}
              hideHeader={isFlowInitiated}
              allowNewPricingSkinnyBanner
            >
              <ProductHead
                ssrOriginPathname={ssrOriginPathname}
                product={product}
              />
              <Loading />
            </ProductMainTemplate>
          )}
        </ByoProvider.Consumer>
      );
    }

    // a bad sets URL won’t always have `products_in_set`
    const hasProductsInSet =
      product.products_in_set && product.products_in_set.length;

    if (productType === SETS && !hasProductsInSet) {
      return <ErrorPage statusCode={statusCode || 404} />;
    }

    const context = ['autoHideNav', 'pdp', 'shopping'];

    return (
      <ByoProvider.Consumer>
        {({ isFlowInitiated, clickedFromByoGrids }) => (
          <StyleContext context={context}>
            <ProductMainTemplate
              hideNewsletterModal
              hideFooter={isFlowInitiated}
              hideHeader={isFlowInitiated}
              isModelImageFullScreen={isModelImageFullScreen}
              allowNewPricingSkinnyBanner
            >
              {trackingEnabled && (
                <SearchSpringTracking onReady={this.initializeTracking} />
              )}
              <ProductHead
                ssrOriginPathname={ssrOriginPathname}
                product={product}
              />
              {isFlowInitiated && clickedFromByoGrids ? (
                <>
                  <ByoStickyProgressBar />
                  <ByoExitModal />
                </>
              ) : null}
              <Layout>
                <ToastContext.Consumer>
                  {({ showMessage }) => (
                    <ProductDetailLayout
                      handleModelImageFullScreen={
                        this.handleModelImageFullScreen
                      }
                      isModelImageFullScreen={isModelImageFullScreen}
                      isProductSet={productType === SETS}
                      product={product}
                      shouldShowConfidenceAssets={
                        !isBorderfreeCustomer && !isVip && !isInfluencer
                      }
                      showToastMessage={showMessage}
                    />
                  )}
                </ToastContext.Consumer>
              </Layout>
            </ProductMainTemplate>
          </StyleContext>
        )}
      </ByoProvider.Consumer>
    );
  }
}

function ProductPageWithHooks(props) {
  const oneTrustConsentList = useOneTrustConsent();
  useBraSizeNavigation();
  return <ProductPage {...props} oneTrustConsentList={oneTrustConsentList} />;
}

ProductPageWithHooks.getInitialProps = async context => {
  const { store, query, req } = context;
  const { productId, permalink, ...restOfQuery } = query;
  // By default, we assume the product type is determined by `productType`, however we need to support
  // `/products/<set product id>` _and_ `/products/sets/<set product id>` for sets. When `productType` does
  // not exist, we first check for a product, then fallback to a product set.
  let { productType } = query;
  let isSetsProduct = productType === SETS;
  let hasAttemptedSetsFallback = false;
  const ssrOriginPathname = process.browser
    ? ''
    : `${req.context.url.origin}${req.context.url.pathname}`;
  let groupCode;
  let getProduct;
  let product;
  let error;

  const retrieveProductSet = async () => {
    product = await store.dispatch(loadProductSet(productId));
    if (!isSetsProduct && !hasAttemptedSetsFallback) {
      isSetsProduct = true;
      hasAttemptedSetsFallback = true;
      productType = SETS;
    }

    const combineLinkedProduct = hasLinkedProduct(product.payload, true);

    const getReviews = product.payload.products_in_set.map(async setItem => {
      const review = store.dispatch(
        loadReviews({
          count: 5,
          masterProductId: setItem.master_product_id,
          shouldUseCache: !!restOfQuery.isClickFromPdp,
          groupCode: setItem.group_code || groupCode,
          isSet: true,
          combineLinkedProduct,
        })
      );
      return review;
    });
    await Promise.all(getReviews);
  };

  const retrieveProduct = async () => {
    getProduct = store.dispatch(loadProduct(productId));
    if (permalink) {
      groupCode = permalink.split('-').slice(-2, -1)[0];
    }

    const getReviews = store.dispatch(
      loadReviews({
        count: 5,
        masterProductId: productId,
        shouldUseCache: !!restOfQuery.isClickFromPdp,
        groupCode,
        combineLinkedProduct: true,
      })
    );

    try {
      [product] = await Promise.all([getProduct, getReviews]);
    } catch (err) {
      if (
        !isSetsProduct &&
        !hasAttemptedSetsFallback &&
        err?.statusCode === 404
      ) {
        await retrieveProductSet();
      } else {
        error = err;
      }
    }
  };

  if (isSetsProduct) {
    await retrieveProductSet();
  } else {
    await retrieveProduct();
  }

  if (!product || !product.payload || error) {
    return {
      product: null,
      statusCode: 404,
      error: product && product.payload,
      query,
    };
  }

  await store.dispatch(loadProductAddedToBasketCount(productId));

  // Format product url based on API response
  const properUrl = formatProductUrl({
    productId,
    permalink: product.payload.permalink,
    productType,
  });

  // Format product url based on query
  const currentUrl = formatProductUrl({ permalink, productId, productType });

  // If current URL deviates from properUrl, redirect to properUrl
  if (currentUrl.as !== properUrl.as) {
    // Build redirect href and as to include additional parameters if present
    const redirectHref = {
      ...properUrl.href,
      query: { ...properUrl.href.query, ...restOfQuery },
    };
    const redirectAs = format({ pathname: properUrl.as, query: restOfQuery });

    return redirect(context.res, redirectHref, redirectAs, 301, {
      shallow: true,
    });
  }

  return {
    product: product.payload,
    isPreorderable: isPreorderable(store.getState(), {
      product: product.payload,
    }),
    productType,
    query,
    ssrOriginPathname,
  };
};

export default createPage(withCookies(ProductPageWithHooks), {
  assetFilter(parentFilter, props) {
    if (!props.product) {
      return parentFilter;
    }
    return (asset, index, assets) => {
      if (!parentFilter(asset, index, assets)) {
        return false;
      }
      // Case fall-through is intentional! `fplid` filtering should happen for
      // both containers.
      /* eslint-disable no-fallthrough */
      switch (asset.container.name) {
        case 'skinny_banner':
        case 'pdp_html_fpl': {
          const fplId = asset.options.customVars.fplid;
          if (fplId) {
            return (
              props.product.featured_product_location_id_list &&
              props.product.featured_product_location_id_list.includes(fplId)
            );
          }
        }
        default:
          return true;
      }
      /* eslint-enable no-fallthrough */
    };
  },
  prefetchAssets: [
    'pdp_html_fpl',
    'pdp_image_vip_box_registration',
    'pdp_html_vip_box_registration_text',
  ],
  prefetchFeatures: ['fab_mobile', 'match_user_size_with_model_size'],
  resourceBundles: [
    'site_pdp',
    'site_account_billship',
    'vip_box_registration',
  ],
  mapStateToProps: state => {
    const defaultCountries = config.get('public.domain.defaultCountries');
    const countryCode = defaultCountries[state.domain.tld];

    return {
      countryCode,
      customerDetails: state.customer,
      domain: state.domain,
      geoCountry: state.intl.geoCountry,
      getDomainRedirect: getDomainRedirectFunction(state),
      isBorderfreeCustomer: state.borderfree.isBorderfreeCustomer,
      isInfluencer: !!state.customer.isInfluencer,
      isLoggedIn: getSession(state).isLoggedIn,
      isUnlockedXclusiveProductVisible: getIsUnlockedXclusiveVisible(state),
      isVip: getMembership(state).isVip,
      isVisitor: getSession(state).isVisitor,
      membershipLoaded:
        !getSession(state).isServerOnly &&
        getMembership(state).networkStatus.isUpToDate,
      noRedirect: state.borderfree.noRedirect,
      reviews: state.reviews || {},
      sessionId: state.session.sessionId,
      storeGroupId: state.storeGroup.storeGroupId,
    };
  },
  mapDispatchToProps: {
    loadProduct,
    loadProductSet,
    loadProductByPermalink,
    trackPdpView,
  },
});
